import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Code2, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "", remember: true });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      login(data.user, data.token);
      toast.success("Login berhasil");
      const dest = data.user.role === "admin" ? "/admin/dashboard" : "/products";
      navigate(location.state?.from && data.user.role !== "admin" ? location.state.from : dest);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Selamat Datang Kembali" subtitle="Masuk untuk mengakses dashboard & produk Anda.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email / Username">
          <input required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" className="input" placeholder="admin@example.com" />
        </Field>
        <Field label="Password">
          <div className="relative">
            <input required type={show ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" className="input pr-10" placeholder="••••••••" />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
          </div>
        </Field>
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 text-slate-600"><input type="checkbox" checked={form.remember} onChange={(e) => setForm({ ...form, remember: e.target.checked })} className="rounded border-slate-300" /> Ingat saya</label>
          <Link to="/forgot-password" className="font-medium text-indigo-600 hover:underline">Lupa Password?</Link>
        </div>
        <button type="submit" disabled={busy} data-testid="login-form-submit-button" className="btn-primary w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}</button>
      </form>

      <p className="mt-6 text-center text-sm text-slate-500">Belum punya akun? <Link to="/register" className="font-semibold text-indigo-600 hover:underline">Daftar sekarang</Link></p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #4F46E5 0, transparent 45%), radial-gradient(circle at 70% 80%, #0EA5E9 0, transparent 45%)" }} />
        <Link to="/" className="relative flex items-center gap-2"><span className="grid place-items-center h-9 w-9 rounded-lg bg-indigo-600 text-white"><Code2 className="h-5 w-5" /></span><span className="font-heading font-extrabold text-lg text-white">DS <span className="text-indigo-400">KODE</span></span></Link>
        <div className="relative">
          <h2 className="font-heading text-3xl font-extrabold text-white leading-tight">Marketplace Digital untuk Developer & Kreator</h2>
          <p className="text-slate-300 mt-3">Aplikasi, source code, template & script berkualitas — siap pakai, aman, dan mudah diunduh.</p>
        </div>
        <p className="relative text-xs text-slate-500">© {new Date().getFullYear()} DS KODE</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8"><span className="grid place-items-center h-9 w-9 rounded-lg bg-indigo-600 text-white"><Code2 className="h-5 w-5" /></span><span className="font-heading font-extrabold text-lg text-slate-900">DS <span className="text-indigo-600">KODE</span></span></Link>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">{title}</h1>
          <p className="text-slate-500 mt-1 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>{children}</div>;
}
